import React, { useEffect, useState } from 'react';
import { FaFacebookF, FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa';
import { HiOutlineHeart } from 'react-icons/hi2';
import './css/Home.css';

const imageUrl = (fileName) => `https://aqgeoubahtnbzgfglgky.supabase.co/storage/v1/object/public/images/${fileName}`;
const videoUrl = (fileName) => `https://aqgeoubahtnbzgfglgky.supabase.co/storage/v1/object/public/videos/${fileName}`;

const featureLinks = [
    {
        title: 'Ken & Bryn - Exclusive Members-Only Content',
        description: 'Join for exclusive members-only content, including full-length dance performances, behind-the-scenes footage, and more.',
        href: 'https://members.kenandbryn.dance',
        thumbnail: imageUrl('dance-performance.jpeg'),
        external: true,
    },
    {
        title: 'Follow Our Story on Ko-fi',
        href: 'https://gift.kenandbryn.dance',
        icon: <HiOutlineHeart />,
        thumbnail: imageUrl('ken-bryn-profile.jpeg'),
        external: true,
    },
];

const socialLinks = [
    {
        label: 'YouTube',
        href: 'https://www.youtube.com/channel/UCDPPfrCYMXon-KCSRfKymTQ',
        icon: <FaYoutube />,
    },
    {
        label: 'Instagram',
        href: 'https://www.instagram.com/kenandbryn/',
        icon: <FaInstagram />,
    },
    {
        label: 'TikTok',
        href: 'https://www.tiktok.com/@kenandbryn',
        icon: <FaTiktok />,
    },
    {
        label: 'Facebook',
        href: 'https://www.facebook.com/kenandbryn/',
        icon: <FaFacebookF />,
    },
];

const Home = () => {
    const [scrollProgress, setScrollProgress] = useState(0);
    const [isHeroVideoLoaded, setIsHeroVideoLoaded] = useState(false);
    const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

    useEffect(() => {
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        let idleCallbackId;
        let fallbackTimerId;

        if (reducedMotion?.matches || connection?.saveData) {
            return undefined;
        }

        const enableVideo = () => {
            if (window.requestIdleCallback) {
                idleCallbackId = window.requestIdleCallback(() => setShouldLoadVideo(true), { timeout: 2000 });
            } else {
                fallbackTimerId = window.setTimeout(() => setShouldLoadVideo(true), 500);
            }
        };

        if (document.readyState === 'complete') {
            enableVideo();
        } else {
            window.addEventListener('load', enableVideo, { once: true });
        }

        return () => {
            window.removeEventListener('load', enableVideo);
            if (idleCallbackId !== undefined) window.cancelIdleCallback?.(idleCallbackId);
            if (fallbackTimerId !== undefined) window.clearTimeout(fallbackTimerId);
        };
    }, []);

    useEffect(() => {
        const updateScrollProgress = () => {
            const progress = Math.min(window.scrollY / 280, 1);
            setScrollProgress(progress);
        };

        updateScrollProgress();
        window.addEventListener('scroll', updateScrollProgress, { passive: true });

        return () => window.removeEventListener('scroll', updateScrollProgress);
    }, []);

    const heroStyle = {
        '--profile-image-opacity': Math.max(1 - scrollProgress * 1.08, 0),
    };

    return (
        <main className={`links-page${isHeroVideoLoaded ? ' links-page-video-ready' : ''}`}>
            {shouldLoadVideo && (
                <video
                    className="links-background-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                    onLoadedData={() => setIsHeroVideoLoaded(true)}
                >
          <source src={videoUrl('social-hero.mp4')} type="video/mp4" />
                </video>
            )}

            <div className="links-hero" style={heroStyle}>
                <section className="links-profile" aria-labelledby="links-title">
                    <div className="links-profile-image-wrap">
                        <img
                            className="links-avatar"
                            src={imageUrl('ken-bryn-profile.jpeg')}
                            alt="Ken and Bryn"
                            width="640"
                            height="640"
                            decoding="async"
                            fetchpriority="high"
                        />
                    </div>

                    <div className="links-profile-overlay">
                        <div className="links-profile-copy">
                            <h1 id="links-title">Ken & Bryn</h1>
                            <p className="links-intro">Dancers. Teachers. World Travelers.</p>
                        </div>

                        <section className="social-link-grid" aria-label="Social links">
                            {socialLinks.map((link) => (
                                <a
                                    className="social-link"
                                    href={link.href}
                                    key={link.label}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={link.label}
                                    title={link.label}
                                >
                                    {link.icon}
                                </a>
                            ))}
                        </section>
                    </div>
                </section>
            </div>

            <section className="links-list" aria-label="Featured links">
                {featureLinks.map((link, index) => (
                    <a
                        className={`feature-link${link.description ? ' feature-link-rich' : ''}`}
                        href={link.href}
                        key={link.title}
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noopener noreferrer' : undefined}
                        style={{ '--link-index': index }}
                    >
                        <span className="feature-link-media" aria-hidden="true">
                            {link.thumbnail ? (
                                <img src={link.thumbnail} alt="" width="66" height="66" loading="lazy" decoding="async" />
                            ) : (
                                <span className="feature-link-icon">{link.icon}</span>
                            )}
                        </span>
                        <span className="feature-link-copy">
                            <span className="feature-link-title">{link.title}</span>
                            {link.description && (
                                <span className="feature-link-description">{link.description}</span>
                            )}
                        </span>
                    </a>
                ))}
            </section>
        </main>
    );
};

export default Home;
